<template>
    <vx-table-row>
        <vx-table-row-title>
            <vx-badge color="slate" size="xs" class="font-mono" data-test="campaign-id">
                {{ props.modelValue.campaign_id }}
            </vx-badge>
        </vx-table-row-title>

        <vx-table-row-item>
            <template v-slot:label> Source: </template>
            <span data-test="source">{{ sourceLabels[props.modelValue.source] }}</span>
        </vx-table-row-item>

        <vx-table-row-item data-test="brand">
            <template v-slot:label> Brand: </template>
            <kiosk-tcr-brand-cell
                v-if="props.modelValue.brand_id"
                :brandId="props.modelValue.brand_id"
                :displayName="props.modelValue.brand_display_name"
            ></kiosk-tcr-brand-cell>
            <span v-else>—</span>
        </vx-table-row-item>

        <vx-table-row-item>
            <template v-slot:label> Brand Trust: </template>
            <span data-test="brand-trust">{{
                props.modelValue.brand_id
                    ? verificationLabel(props.modelValue.brand_verified, props.modelValue.brand_vetting_score)
                    : '—'
            }}</span>
        </vx-table-row-item>

        <vx-table-row-item>
            <template v-slot:label> Use Case: </template>
            <span data-test="usecase">{{
                props.modelValue.usecase ? useCaseLabels[props.modelValue.usecase] : '—'
            }}</span>
        </vx-table-row-item>

        <vx-table-row-item>
            <template v-slot:label> Status: </template>
            <span data-test="status">{{
                props.modelValue.status ? campaignStatusLabels[props.modelValue.status] : '—'
            }}</span>
        </vx-table-row-item>

        <vx-table-row-item>
            <template v-slot:label> DCA Complete: </template>
            <vx-badge
                v-if="dca"
                :color="dca.color"
                size="xs"
                class="whitespace-nowrap"
                :title="dca.title"
                data-test="dca-complete"
            >
                {{ dca.label }}
            </vx-badge>
            <span v-else data-test="dca-complete">N/A</span>
        </vx-table-row-item>

        <vx-table-row-item>
            <template v-slot:label> AT&T Class: </template>
            <span
                v-if="props.modelValue.att_message_class"
                :title="messageClassDetail(props.modelValue.att_message_class) ?? undefined"
                data-test="message-class"
            >
                {{ props.modelValue.att_message_class }}
            </span>
            <span v-else>—</span>
        </vx-table-row-item>

        <vx-table-row-item>
            <template v-slot:label> T-Mobile Tier: </template>
            <span data-test="tmobile-tier">{{
                props.modelValue.tmobile_brand_tier ? tmobileBrandTierLabels[props.modelValue.tmobile_brand_tier] : '—'
            }}</span>
        </vx-table-row-item>

        <vx-table-row-item>
            <template v-slot:label> Terms: </template>
            <vx-badge
                v-if="props.modelValue.better_terms_available"
                color="info"
                size="xs"
                class="whitespace-nowrap"
                :title="availableTermsLabel(props.modelValue) || undefined"
                data-test="terms"
            >
                {{ termsLabel(props.modelValue.better_terms_available) }}
            </vx-badge>
            <span v-else data-test="terms">{{
                'twilio' === props.modelValue.source ? 'N/A' : termsLabel(props.modelValue.better_terms_available)
            }}</span>
        </vx-table-row-item>

        <vx-table-row-item data-test="phone-numbers-count">
            <template v-slot:label> Numbers: </template>
            {{ props.modelValue.phone_numbers_count }}
        </vx-table-row-item>

        <vx-table-row-buttons>
            <vx-button
                v-if="hasRegistration"
                :href="`/kiosk/ten-dlc/campaigns/${props.modelValue.campaign_id}`"
                size="sm"
                data-test="registration"
                color="primary"
            >
                Registration <font-awesome-icon widthAuto :icon="faTowerCell" />
            </vx-button>
            <vx-button :href="phoneNumbersHref" size="sm" data-test="view" outline color="muted">
                View <font-awesome-icon widthAuto :icon="faEye" />
            </vx-button>
        </vx-table-row-buttons>
    </vx-table-row>
</template>

<script lang="ts" setup>
import { faEye, faTowerCell } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
    VxBadge,
    VxButton,
    VxTableRow,
    VxTableRowButtons,
    VxTableRowItem,
    VxTableRowTitle,
} from '@voxie/frontend-components';
import { computed } from 'vue';
import KioskTcrBrandCell from '~/components/kiosk/kiosk-ten-dlc/list/KioskTcrBrandCell.vue';
import {
    availableTermsLabel,
    campaignStatusLabels,
    dcaCompletion,
    messageClassDetail,
    sourceLabels,
    termsLabel,
    tmobileBrandTierLabels,
    useCaseLabels,
    verificationLabel,
} from '~/components/kiosk/kiosk-ten-dlc/tenDlcLabels';
import type { TcrCampaign } from '~/services/adminTenDlcClient.types';
import { stringifyQueryString } from '~/utils/string';

const props = withDefaults(
    defineProps<{
        modelValue: TcrCampaign;
        registrationEnabled?: boolean;
    }>(),
    { registrationEnabled: false }
);

const dca = computed(() => dcaCompletion(props.modelValue));

// A webhook can create the row before the sync mirrors the campaign, and TCR may not hold such a campaign.
const hasRegistration = computed(
    () => props.registrationEnabled && 'tcr' === props.modelValue.source && null !== props.modelValue.brand_id
);

const phoneNumbersHref = computed(
    () => `/kiosk/ten-dlc/phone-numbers?${stringifyQueryString({ campaign_id: props.modelValue.campaign_id })}`
);
</script>
